/* platform/Workspaces — SA-level view of all workspaces across all orgs.
   Route: /platform/workspaces */
import React, { useState, useEffect, useCallback } from 'react';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar, Th, TenancyModal } from './_ui';
import { getPlatformRole } from './rbac.js';
import { statusTone } from './platformStore';
import * as wsApi from '../api/platform-workspaces.api.js';
import * as auditApi from '../api/audit.api.js';

const WORKSPACE_TABS = [
  { id: 'overview',  label: 'Overview',  icon: 'Home'        },
  { id: 'members',   label: 'Members',   icon: 'Users'       },
  { id: 'settings',  label: 'Settings',  icon: 'Settings'    },
  { id: 'audit',     label: 'Audit',     icon: 'Activity'    },
];

const STATUS_OPTS = ['ACTIVE','ONBOARDING','SUSPENDED','ARCHIVED'];

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

function WorkspaceDetail({ ws, onClose, onRefresh, isSA }) {
  const toast = useToast();
  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    wsApi.getOne(ws.id)
      .then(d => { setDetail(d); setEditForm({ name: d.name }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ws.id]);

  useEffect(() => {
    if (tab !== 'audit') return;
    setAuditLoading(true);
    auditApi.list({ limit: 200 })
      .then(res => {
        const all = Array.isArray(res) ? res : (res?.data ?? []);
        setAuditEvents(all.filter(e => e.target === ws.name || e.target === ws.id));
      })
      .catch(() => setAuditEvents([]))
      .finally(() => setAuditLoading(false));
  }, [tab, ws.id, ws.name]);

  const doAction = async (action, successMsg) => {
    setBusy(true);
    try {
      await action();
      toast({ tone: 'success', icon: 'Check', title: successMsg });
      onRefresh();
      onClose();
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err?.message }); }
    finally { setBusy(false); }
  };

  const handleSaveEdit = () => doAction(
    () => wsApi.update(ws.id, editForm),
    'Workspace updated'
  ).then(() => setEditModal(false));

  const d = detail || ws;

  return (
    <div className="nx-cc-detail-slide" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 640, background: 'var(--nx-bg)', borderLeft: '1px solid var(--nx-border)', zIndex: 120, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="nx-domain-mark" style={{ background: '#125AB018', color: '#125AB0', width: 36, height: 36, fontSize: 14 }}>
          {(d.name || 'W')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--nx-text)' }}>{d.name}</div>
          <div className="nx-muted nx-mini">{d.org?.name || '—'} · {d.id?.slice(0,8)}…</div>
        </div>
        <Pill tone={statusTone(d.status)}>{(d.status || 'active').toLowerCase()}</Pill>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4 }}><NexusIcon name="X" size={16} /></button>
      </div>

      {isSA && (
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="sm" icon="Pencil"      onClick={() => setEditModal(true)}>Edit</Btn>
          {(d.status || '').toUpperCase() !== 'ACTIVE'   && <Btn variant="ghost" size="sm" icon="Check"       onClick={() => doAction(() => wsApi.activate(ws.id), 'Workspace activated')}>Activate</Btn>}
          {(d.status || '').toUpperCase() === 'ACTIVE'   && <Btn variant="ghost" size="sm" icon="AlertCircle" onClick={() => doAction(() => wsApi.suspend(ws.id), 'Workspace suspended')}>Suspend</Btn>}
          {(d.status || '').toUpperCase() !== 'ARCHIVED' && <Btn variant="ghost" size="sm" icon="Archive"     onClick={() => doAction(() => wsApi.archive(ws.id), 'Workspace archived')}>Archive</Btn>}
          <Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm(true)}>Delete</Btn>
        </div>
      )}

      <TabBar tabs={WORKSPACE_TABS} active={tab} onChange={setTab} />

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {loading && <div className="nx-empty"><NexusIcon name="Loader" size={16} /><span>Loading…</span></div>}

        {!loading && tab === 'overview' && (
          <div>
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginBottom: 14 }}>
              <StatCard label="Members"   value={d._count?.memberships ?? d.memberships?.length ?? 0} icon="Users"    color="#125AB0" />
              <StatCard label="Created"   value={fmt(d.createdAt)}                                    icon="Calendar" color="#0E8C5A" />
            </div>
            <div className="nx-form" style={{ maxWidth: '100%' }}>
              {[
                ['Workspace ID', d.id],
                ['Organization', d.org?.name || '—'],
                ['Status',       (d.status || '').toLowerCase()],
                ['Created',      fmt(d.createdAt)],
                ['Updated',      fmt(d.updatedAt)],
              ].map(([label, value]) => (
                <div key={label} className="nx-form-row">
                  <label>{label}</label>
                  <span>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && tab === 'members' && (
          <div>
            {(!d.memberships || d.memberships.length === 0)
              ? <div className="nx-empty"><NexusIcon name="Users" size={20} /><span>No members in this workspace.</span></div>
              : <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                  <thead><tr>
                    <Th icon="User">Name</Th>
                    <Th icon="Mail">Email</Th>
                    <Th icon="Shield">Role</Th>
                    <Th icon="Calendar">Joined</Th>
                  </tr></thead>
                  <tbody>{(d.memberships || []).map(m => (
                    <tr key={m.id} className="nx-tr">
                      <td><span className="nx-row-name-text">{m.member?.name || '—'}</span></td>
                      <td><span className="nx-muted">{m.member?.email || '—'}</span></td>
                      <td><Pill tone="blue">{(m.wsRole || '—').replace('WORKSPACE_', '')}</Pill></td>
                      <td><span className="nx-muted nx-mini">{fmt(m.createdAt)}</span></td>
                    </tr>
                  ))}</tbody>
                </table></div></div>
            }
          </div>
        )}

        {!loading && tab === 'settings' && (
          <div className="nx-form" style={{ maxWidth: 400 }}>
            <div className="nx-form-row"><label>Name</label><span>{d.name}</span></div>
            <div className="nx-form-row"><label>Status</label><Pill tone={statusTone(d.status)}>{(d.status || 'active').toLowerCase()}</Pill></div>
            {isSA && <Btn variant="secondary" icon="Pencil" onClick={() => setEditModal(true)} style={{ marginTop: 12 }}>Edit Workspace</Btn>}
          </div>
        )}

        {!loading && tab === 'audit' && (
          <div>
            {auditLoading && <div className="nx-empty"><NexusIcon name="Loader" size={16} /><span>Loading audit log…</span></div>}
            {!auditLoading && auditEvents.length === 0 && (
              <div className="nx-empty"><NexusIcon name="Activity" size={20} /><span>No audit events for this workspace yet.</span></div>
            )}
            {!auditLoading && auditEvents.length > 0 && (
              <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                <thead><tr>
                  <Th icon="Activity">Action</Th>
                  <Th icon="User">Who</Th>
                  <Th icon="Tag">Category</Th>
                  <Th icon="Calendar">When</Th>
                </tr></thead>
                <tbody>{auditEvents.map((e, i) => (
                  <tr key={e.id || i} className="nx-tr">
                    <td><span className="nx-row-name-text">{e.action}</span></td>
                    <td><span className="nx-muted">{e.who || '—'}</span></td>
                    <td><span className="nx-muted">{e.cat || '—'}</span></td>
                    <td><span className="nx-muted nx-mini">{fmt(e.createdAt)}</span></td>
                  </tr>
                ))}</tbody>
              </table></div></div>
            )}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editModal && (
        <TenancyModal title="Edit Workspace" icon="Pencil" onClose={() => setEditModal(false)}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Name</label>
              <input className="nx-input" value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setEditModal(false)}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={busy} onClick={handleSaveEdit}>{busy ? 'Saving…' : 'Save'}</Btn>
          </div>
        </TenancyModal>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <TenancyModal title={'Delete Workspace — ' + d.name + '?'} icon="Trash" danger onClose={() => setDeleteConfirm(false)}>
          <p className="nx-confirm-desc">This permanently deletes the workspace and all its data. This action cannot be undone.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" disabled={busy} onClick={() => doAction(() => wsApi.remove(ws.id), 'Workspace deleted')}>Delete</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}

export default function Workspaces() {
  const toast  = useToast();
  const myRole = getPlatformRole();
  const isSA   = myRole === 'SUPER_ADMIN';

  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [q, setQ]                   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    wsApi.listAll()
      .then(res => setWorkspaces(Array.isArray(res) ? res : (res?.data ?? [])))
      .catch(() => setWorkspaces([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = workspaces.filter(w => {
    const matchQ = !q || (w.name || '').toLowerCase().includes(q.toLowerCase()) || (w.organization?.name || '').toLowerCase().includes(q.toLowerCase());
    const matchS = !statusFilter || (w.status || '').toUpperCase() === statusFilter;
    return matchQ && matchS;
  });

  const total    = workspaces.length;
  const active   = workspaces.filter(w => (w.status || '').toUpperCase() === 'ACTIVE').length;
  const archived = workspaces.filter(w => (w.status || '').toUpperCase() === 'ARCHIVED').length;
  const boarding = workspaces.filter(w => (w.status || '').toUpperCase() === 'ONBOARDING').length;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Workspaces"
        sub="All workspaces across all organizations — SA view"
      />

      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
        <StatCard label="Total"      value={total}    icon="LayoutGrid" color="#125AB0" />
        <StatCard label="Active"     value={active}   icon="Check"      color="#0E8C5A" />
        <StatCard label="Onboarding" value={boarding}  icon="Clock"      color="#E08A00" />
        <StatCard label="Archived"   value={archived} icon="Archive"    color="#8B949E" />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div className="nx-search-inline" style={{ width: 300 }}>
          <NexusIcon name="Search" size={13} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or organization…" />
        </div>
        <select className="nx-input" style={{ width: 160, fontSize: 13 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
        </select>
        <Btn variant="ghost" icon="RefreshCw" onClick={load}>Refresh</Btn>
      </div>

      {loading ? (
        <div className="nx-empty"><NexusIcon name="Loader" size={20} /><span>Loading workspaces…</span></div>
      ) : filtered.length === 0 ? (
        <div className="nx-empty">
          <NexusIcon name="LayoutGrid" size={24} />
          <span>{q || statusFilter ? 'No workspaces match your filters.' : 'No workspaces found.'}</span>
        </div>
      ) : (
        <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
          <thead><tr>
            <Th icon="LayoutGrid">Workspace</Th>
            <Th icon="Building2">Organization</Th>
            <Th icon="Activity">Status</Th>
            <Th icon="Users">Members</Th>
            <Th icon="Calendar">Created</Th>
            <Th end />
          </tr></thead>
          <tbody>{filtered.map(w => (
            <tr key={w.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => setSelected(w)}>
              <td>
                <span className="nx-row-name">
                  <span className="nx-domain-mark" style={{ width: 28, height: 28, fontSize: 11, background: '#125AB018', color: '#125AB0' }}>
                    {(w.name || 'W')[0].toUpperCase()}
                  </span>
                  <div>
                    <div className="nx-row-name-text">{w.name}</div>
                    <span className="nx-muted nx-mini">{w.id?.slice(0, 8)}…</span>
                  </div>
                </span>
              </td>
              <td><span className="nx-muted">{w.org?.name || '—'}</span></td>
              <td><Pill tone={statusTone(w.status)}>{(w.status || 'active').toLowerCase()}</Pill></td>
              <td><span className="nx-muted">{w._count?.memberships ?? '—'}</span></td>
              <td><span className="nx-muted nx-mini">{fmt(w.createdAt)}</span></td>
              <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                <Btn variant="ghost" size="sm" icon="ChevronRight" onClick={() => setSelected(w)}>View</Btn>
              </td>
            </tr>
          ))}</tbody>
        </table></div></div>
      )}

      {/* Detail slide-over */}
      {selected && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 119 }} onClick={() => setSelected(null)} />
          <WorkspaceDetail ws={selected} onClose={() => setSelected(null)} onRefresh={load} isSA={isSA} />
        </>
      )}
    </div>
  );
}
